import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'sonner';
import { createProgressClient, type ProgressState, type Scope, type TourId } from './progressClient';
import './walkthroughs.css';
import { canExploreSignupDashboard, isPendingSignup } from '../signup/activation';

export interface ActiveGuide { id: TourId; runId: string }
interface WalkthroughContextValue extends ProgressState {
  activationPending: boolean;
  canInviteTeammates: boolean;
  guidesEnabled: boolean;
  guide: ActiveGuide | null;
  reload: () => void;
  start: (id: TourId) => Promise<boolean>;
  finish: (action: 'dismiss' | 'complete') => Promise<boolean>;
  dismissWelcome: () => void;
}
const Context = createContext<WalkthroughContextValue | null>(null);
const empty: ProgressState = { data: null, loading: true, busy: false, error: null };

export function WalkthroughProvider({ children, guidesEnabled = true }: { children: ReactNode; guidesEnabled?: boolean }) {
  const canInviteTeammates = useSelector((state: { auth?: Parameters<typeof isPendingSignup>[0] }) => !isPendingSignup(state.auth) || state.auth?.user?.accessLevel === 'owner');
  const exploringSignup = useSelector((state: { auth?: Parameters<typeof isPendingSignup>[0] }) => canExploreSignupDashboard(state.auth));
  const activationPending = useSelector((state: { auth?: Parameters<typeof isPendingSignup>[0] }) => isPendingSignup(state.auth));
  const scope = useSelector((state: { auth?: { _id?: string; user?: { _id?: string } } }) => ({
    businessId: state.auth?._id || '', userId: state.auth?.user?._id || '',
  }), (a, b) => a.businessId === b.businessId && a.userId === b.userId);
  // Remount before rendering a different membership; old requests cannot paint the new dashboard.
  return <ScopedProvider key={`${scope.businessId}:${scope.userId}`} scope={scope} activationPending={activationPending} canInviteTeammates={canInviteTeammates} guidesEnabled={exploringSignup || (guidesEnabled && !activationPending)}>{children}</ScopedProvider>;
}

function ScopedProvider({ scope, children, guidesEnabled, activationPending, canInviteTeammates }: { scope: Scope; children: ReactNode; guidesEnabled: boolean; activationPending: boolean; canInviteTeammates: boolean }) {
  const [state, setState] = useState(empty);
  const [guide, setGuide] = useState<ActiveGuide | null>(null);
  const client = useMemo(() => createProgressClient(scope, async (method, url, body) => {
    // App's shared axios interceptor supplies the current authenticated JWT.
    // Scope assertions let the server reject queued clicks if that JWT changed meanwhile.
    const config = { timeout: 15000, headers: { 'X-Walkthrough-Business': scope.businessId, 'X-Walkthrough-User': scope.userId } };
    const response = method === 'GET' ? await axios.get(url, config) : await axios.post(url, body, config);
    return response.data;
  }, setState), [scope.businessId, scope.userId]);

  useEffect(() => {
    client.activate();
    if (scope.businessId && scope.userId) void client.load();
    return () => client.dispose();
  }, [client, scope.businessId, scope.userId]);
  const start = useCallback(async (id: TourId) => {
    if (guide || !guidesEnabled || (id === 'team' && !canInviteTeammates)) return false;
    const runId = crypto.randomUUID();
    const response = await client.start(id, runId);
    if (!response || response.progress.tours[id]?.runId !== runId || response.progress.tours[id]?.status !== 'started') return false;
    setGuide({ id, runId }); return true;
  }, [client, guide, guidesEnabled, canInviteTeammates]);
  const finish = useCallback(async (action: 'dismiss' | 'complete') => {
    if (!guide) return false;
    const response = await client.finish(guide.id, guide.runId, action);
    if (!client.isActive()) return false;
    if (response || action === 'dismiss') setGuide(null);
    if (!response) toast.error('We couldn’t save your progress. You can try again from Walkthroughs.');
    return !!response;
  }, [client, guide]);
  // A refresh after a conflict may show another tab's run. Never complete that run here.
  useEffect(() => {
    if (guide && state.data && (state.data.progress.tours[guide.id]?.runId !== guide.runId || state.data.progress.tours[guide.id]?.status !== 'started')) setGuide(null);
  }, [state.data, guide]);
  const value = useMemo(() => ({ ...state, guidesEnabled, activationPending, canInviteTeammates, guide, start, finish,
    reload: () => { void client.load(); },
    dismissWelcome: () => { void client.dismissWelcome(); },
  }), [state, guidesEnabled, activationPending, canInviteTeammates, guide, start, finish, client]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useWalkthroughs() {
  const context = useContext(Context);
  if (!context) throw new Error('WalkthroughProvider is required');
  return context;
}
